import {
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type Ref,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  LANE_LABEL_COLUMN_PX,
  type CalendarGridModel,
  type PlatformLane,
} from './grid-model';
import {
  laneFocusItems,
  layoutLane,
  type LaneFocusItem,
  type LaneLayout,
  type SwimlaneMilestone,
} from './lane-layer';
import { InitiativeBar } from './InitiativeBar';
import { MilestoneSwimlane } from './MilestoneSwimlane';
import { CalendarLegend } from './CalendarLegend';
import { LaneOverflowIndicator } from './LaneOverflowIndicator';
import { useRovingCalendarFocus, type RovingItem } from './use-roving-calendar-focus';
import type { FocusGrid } from './calendar-keyboard-model';

/**
 * The Calendar_View grid (task 22.2 scaffolding + task 22.3 bar/milestone layer).
 *
 * Task 22.2 laid out the four bands — header, Sales Cycle band, Platform lane rows
 * and their empty tracks — and left a per-lane track div as the seam for the bar
 * and milestone layer. Task 22.3 fills that seam: each lane's bars are positioned
 * and clipped through the domain geometry, stacked into non-overlapping sub-rows,
 * capped with an overflow indicator, and their milestones placed — all via the
 * pure {@link layoutLane}. The mandatory legend (§4.5) anchors the bottom.
 *
 * ## Why a measured column width
 *
 * The domain assigns stacking lanes on *rendered pixel* intervals, not date
 * intervals, because R10.4's minimum bar width can make two date-disjoint bars
 * overlap on screen. So the layer needs the real column width, which only the DOM
 * knows. The track width is measured with a `ResizeObserver`; until the first
 * measurement the lanes render at a sensible fallback width, then recompute. This
 * is a pure re-derivation over cached data — it never refetches (R10.2).
 *
 * The lane rows are virtualised (TanStack Virtual 3). Because stacking makes lane
 * heights variable (§4.3: `max(1, sub-rows) × 30 + 12`px), each row measures
 * itself back to the virtualizer rather than using a fixed estimate.
 */

/** The Sales Cycle band row height (UI spec §4.1), plus spacing for visual separation. */
const SC_BAND_HEIGHT_PX = 30;

/** The header row height. */
const HEADER_ROW_HEIGHT_PX = 34;

/** A single-sub-row lane before measurement: `1 × 30 + 12` (§4.3). */
const FALLBACK_LANE_HEIGHT_PX = 42;

/** A sensible column width before the track is first measured, so lanes can lay out. */
const FALLBACK_COLUMN_WIDTH_PX = 60;

/**
 * The cap the R10.15 expand action re-runs a lane with: the domain's maximum
 * permitted cap (50). Expanding renders every bar the maximum cap fits without
 * touching any applied filter; a lane denser than 50 sub-rows scrolls within the
 * grid, which the overflow indicator still surfaces.
 */
const EXPANDED_ROW_CAP = 50;

/**
 * The imperative handle the parent uses to return focus to the calendar when the
 * Detail_Panel closes (task 22.4, §9 gap #2). The parent holds a ref to it and
 * calls {@link CalendarGridHandle.notifyDetailClosed} once the panel has closed.
 */
export interface CalendarGridHandle {
  readonly notifyDetailClosed: () => void;
}

export interface CalendarGridProps {
  readonly model: CalendarGridModel;
  /**
   * Opens the Detail_Panel for an Initiative (pointer click or keyboard Enter /
   * Space on the focused bar, R10.13).
   */
  readonly onOpenInitiative: (initiativeId: string) => void;
  /** The stacking row cap (R10.15); defaults to the domain default inside the layer. */
  readonly rowCap?: number | undefined;
  /** Imperative handle for returning focus after the Detail_Panel closes (task 22.4). */
  readonly handleRef?: Ref<CalendarGridHandle> | undefined;
}

export function CalendarGrid({
  model,
  onOpenInitiative,
  rowCap,
  handleRef,
}: CalendarGridProps): React.JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackWidthRef = useRef<HTMLDivElement>(null);
  const [columnWidthPx, setColumnWidthPx] = useState(FALLBACK_COLUMN_WIDTH_PX);

  // Lanes the R10.15 expand action has revealed (rendered without a cap). Keyed by
  // Platform: expanding one dense lane must not change any applied filter (R10.15).
  const [expandedLanes, setExpandedLanes] = useState<ReadonlySet<string>>(new Set());

  const columnCount = model.columns.length;

  // Measure the rendered track so the domain can assign lanes on real pixels. The
  // ref sits on the full grid inner width; the track is that width minus the fixed
  // lane-label column, divided evenly across the columns (§4.1's `1fr` columns).
  useLayoutEffect(() => {
    const element = trackWidthRef.current;
    if (!element) return;
    const measure = (): void => {
      const trackWidth = element.clientWidth - LANE_LABEL_COLUMN_PX;
      if (trackWidth > 0) {
        setColumnWidthPx(trackWidth / Math.max(columnCount, 1));
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [columnCount]);

  // One lane layout per Platform lane, recomputed when the width, cap or data change.
  const laneLayouts = useMemo(() => {
    const map = new Map<string, LaneLayout>();
    for (const lane of model.platformLanes) {
      map.set(
        lane.platformId,
        layoutLane(lane.bars, {
          track: model.dayTrack,
          salesCycleTrack: model.salesCycleTrack,
          columns: model.columns,
          columnWidthPx,
          rowCap: expandedLanes.has(lane.platformId) ? EXPANDED_ROW_CAP : rowCap,
        }),
      );
    }
    return map;
  }, [model, columnWidthPx, rowCap, expandedLanes]);

  const lanes = model.platformLanes;
  const rowVirtualizer = useVirtualizer({
    count: lanes.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => {
      const lane = lanes[index];
      const layout = lane ? laneLayouts.get(lane.platformId) : undefined;
      return layout?.heightPx ?? FALLBACK_LANE_HEIGHT_PX;
    },
    overscan: 6,
  });

  const gridTemplateColumns = `${String(LANE_LABEL_COLUMN_PX)}px repeat(${String(
    Math.max(columnCount, 1),
  )}, minmax(0, 1fr))`;

  // The keyboard composite widget (task 22.4). Each lane's focusable items — its
  // bars and their milestone markers, in reading order — come from the same pure
  // `laneFocusItems` the row renders from, so the roving `(lane, item)` coordinates
  // and the DOM order cannot drift. The focus grid the model reasons over is just
  // the per-lane item counts, in the same lane order the virtualizer indexes.
  const laneFocusItemsByLane = useMemo(
    () =>
      lanes.map((lane) => {
        const layout = laneLayouts.get(lane.platformId);
        return layout ? laneFocusItems(layout) : [];
      }),
    [lanes, laneLayouts],
  );

  const focusGrid = useMemo<FocusGrid>(
    () => ({ laneItemCounts: laneFocusItemsByLane.map((items) => items.length) }),
    [laneFocusItemsByLane],
  );

  const scrollLaneIntoView = useCallback(
    (lane: number): void => {
      // Bring a lane into view before focus moves to it, since a virtualized row
      // beyond the overscan window is not mounted and so cannot take focus.
      rowVirtualizer.scrollToIndex(lane, { align: 'auto' });
    },
    [rowVirtualizer],
  );

  const roving = useRovingCalendarFocus({
    grid: focusGrid,
    onActivate: (pos) => {
      const item = laneFocusItemsByLane[pos.lane]?.[pos.item];
      if (item) onOpenInitiative(item.placed.bar.id);
    },
    scrollLaneIntoView,
  });

  useImperativeHandle(handleRef, () => ({ notifyDetailClosed: roving.notifyDetailClosed }), [
    roving,
  ]);

  const toggleExpand = useCallback((platformId: string) => {
    setExpandedLanes((current) => {
      const next = new Set(current);
      if (next.has(platformId)) next.delete(platformId);
      else next.add(platformId);
      return next;
    });
  }, []);

  return (
    <div className="border-line overflow-hidden rounded-[var(--radius-card)] border">
      <div ref={scrollRef}>
        <div
          ref={trackWidthRef}
          style={{
            minWidth:
              columnCount > 16 ? `${String(columnCount * 44 + LANE_LABEL_COLUMN_PX)}px` : undefined,
          }}
        >
          <HeaderRow model={model} gridTemplateColumns={gridTemplateColumns} />
          <SalesCycleBandRow model={model} gridTemplateColumns={gridTemplateColumns} />

          <div className="relative" style={{ height: `${String(rowVirtualizer.getTotalSize())}px` }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const lane = lanes[virtualRow.index];
              if (!lane) return null;
              const layout = laneLayouts.get(lane.platformId);
              return (
                <div
                  key={lane.platformId}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  className="absolute left-0 top-0 w-full"
                  style={{ transform: `translateY(${String(virtualRow.start)}px)` }}
                >
                  <LaneRow
                    lane={lane}
                    laneIndex={virtualRow.index}
                    focusItems={laneFocusItemsByLane[virtualRow.index] ?? []}
                    layout={layout}
                    model={model}
                    gridTemplateColumns={gridTemplateColumns}
                    expanded={expandedLanes.has(lane.platformId)}
                    onToggleExpand={() => toggleExpand(lane.platformId)}
                    onOpenInitiative={onOpenInitiative}
                    itemProps={roving.itemProps}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* The mandatory anchored legend (§4.5). */}
      <CalendarLegend lanes={model.platformLanes} showsCurrentWeek={model.currentPeriod !== null} />
    </div>
  );
}

interface BandProps {
  readonly model: CalendarGridModel;
  readonly gridTemplateColumns: string;
}

/** The header row: lane-label cell + one centred, uppercase, muted cell per column. */
function HeaderRow({ model, gridTemplateColumns }: BandProps): React.JSX.Element {
  const { t } = useTranslation();
  const current = model.currentPeriod;
  // Plain `<div>`s laid out with CSS Grid, not ARIA `row`/`columnheader` roles:
  // this widget is a roving-tabindex composite of buttons and markers (task
  // 22.4), not a full WAI-ARIA grid/table (which would additionally require a
  // `rowgroup`/`table`/`grid` ancestor for every `row` and `gridcell`/
  // `columnheader` children rather than bare buttons — the very combination
  // `aria-required-parent`/`aria-required-children` flag). CSS `display: grid`
  // is a visual layout mechanism independent of the ARIA table pattern, so no
  // accessibility semantics are lost by leaving these as plain containers; the
  // column and lane labels below remain in the accessible tree as ordinary text.
  return (
    <div
      className="bg-surface-alt border-line grid border-b"
      style={{ gridTemplateColumns, height: `${String(HEADER_ROW_HEIGHT_PX)}px` }}
    >
      <div className="text-label border-line flex items-center border-r px-[14px] text-[10.5px] font-semibold uppercase tracking-[0.6px]">
        {t('calendar.laneHeader')}
      </div>
      {model.columns.map((column) => {
        const isCurrent =
          current?.currentMonthColumnIndex === column.index ||
          current?.currentIsoWeekColumnIndex === column.index;
        return (
          <div
            key={column.index}
            title={column.fullLabel}
            className={cn(
              'text-label flex items-center justify-center text-[10.5px] font-semibold uppercase tracking-[0.6px]',
              isCurrent && 'text-accent-hover',
            )}
          >
            <span className="flex items-center gap-1">
              {column.label}
              {isCurrent ? <CurrentPeriodDot /> : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The Sales Cycle band row (UI spec §4.1): `#F7F9FC` background, one chip per
 * active cycle spanning the columns it covers, `#E3E9F1` chip on `#3D5573` text.
 */
function SalesCycleBandRow({ model, gridTemplateColumns }: BandProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className="border-line grid border-b"
      style={{
        gridTemplateColumns,
        height: `${String(SC_BAND_HEIGHT_PX)}px`,
        background: 'var(--sc-band-bg)',
        boxShadow: '0 2px 4px rgba(21, 42, 71, 0.06)',
        paddingBottom: '12px',
        marginBottom: '6px',
      }}
    >
      <div className="text-label border-line flex items-center border-r px-[14px] text-[10.5px] font-semibold uppercase tracking-[0.6px]">
        {t('calendar.salesCycleBandHeader')}
      </div>
      {/* The chips sit over the same track the lane bars use - the axis label
          column, then the day-proportional track - so a chip and a bar starting
          on the same date line up. The chip is positioned by fractional column
          units (left/width percentages of the track) rather than snapped to whole
          grid columns, so a cycle ending mid-column stops mid-column. */}
      <div
        className="relative"
        style={{ gridColumn: `2 / ${String(model.columns.length + 2)}` }}
      >
        {model.salesCycleBand.map((chip) => (
          <span
            key={chip.salesCycleId}
            title={chip.fullLabel}
            className="absolute top-[3px] truncate rounded-[var(--radius-badge)] px-2 py-[2px] text-center text-[10.5px] font-semibold"
            style={{
              left: `calc(${String((chip.startColumns / chip.columnCount) * 100)}% + 3px)`,
              width: `calc(${String((chip.widthColumns / chip.columnCount) * 100)}% - 6px)`,
              background: 'var(--sc-chip-bg)',
              color: 'var(--sc-chip-txt)',
            }}
          >
            {chip.label}
          </span>
        ))}
      </div>
    </div>
  );
}

interface LaneRowProps {
  readonly lane: PlatformLane;
  /** This lane's index in the virtualizer / focus grid (task 22.4). */
  readonly laneIndex: number;
  /** The lane's focusable items in reading order — the same order the row renders. */
  readonly focusItems: readonly LaneFocusItem[];
  readonly layout: LaneLayout | undefined;
  readonly model: CalendarGridModel;
  readonly gridTemplateColumns: string;
  readonly expanded: boolean;
  readonly onToggleExpand: () => void;
  readonly onOpenInitiative: (initiativeId: string) => void;
  /** Roving-tabindex props for the item at `(laneIndex, item)` (task 22.4). */
  readonly itemProps: (lane: number, item: number) => RovingItem;
}

/**
 * One Platform lane row (UI spec §4.1). The lane label carries the Platform name
 * and colour swatch; the track cell renders the bar/milestone layer (task 22.3)
 * plus the overflow indicator and the current-period rules.
 *
 * The label shows the Platform's `name` (e.g. "P1"), not its `code` — the
 * lowercase internal identifier master data stores for export round-tripping
 * and uniqueness (`packages/api/migrations/0002_master_data_tables.sql`). R10.5
 * asks for the Platform *name* here, and rendering `code` would leak that
 * internal identifier (and its casing, however an administrator happened to
 * type it) into a user-facing label.
 *
 * The bars and markers render in the order of {@link LaneRowProps.focusItems},
 * the same order the keyboard model walks, and each carries the roving-tabindex
 * props for its `(laneIndex, item)` coordinate (task 22.4). So the DOM order, the
 * single tab stop and the arrow-key movement are all one derivation.
 */
function LaneRow({
  lane,
  laneIndex,
  focusItems,
  layout,
  model,
  gridTemplateColumns,
  expanded,
  onToggleExpand,
  onOpenInitiative,
  itemProps,
}: LaneRowProps): React.JSX.Element {
  const heightPx = layout?.heightPx ?? FALLBACK_LANE_HEIGHT_PX;
  const barsOffsetPx = layout?.barsOffsetPx ?? 0;
  const swimlane = layout?.swimlane ?? [];
  // The bar this lane's swimlane is currently wiring to (a milestone is hovered or
  // focused). Held per lane so highlighting one Platform's milestone never lights
  // up a look-alike bar in another lane.
  const [highlightedBarId, setHighlightedBarId] = useState<string | null>(null);
  return (
    <div
      className="border-line-soft grid border-b"
      style={{ gridTemplateColumns, minHeight: `${String(heightPx)}px` }}
    >
      <div className="border-line flex items-center gap-2 border-r px-[14px]">
        <span
          aria-hidden
          className="inline-block h-[10px] w-[10px] shrink-0 rounded-[2px]"
          style={{ background: lane.colour }}
        />
        <span className="truncate text-[12px] font-semibold" title={lane.name}>
          {lane.name}
        </span>
      </div>

      {/* The lane track: column guides, current-period rules, then the bar and
          milestone layer of task 22.3, and the R10.15 overflow indicator. */}
      <div
        className="relative"
        style={{ gridColumn: `2 / ${String(model.columns.length + 2)}` }}
        data-lane-track={lane.platformId}
      >
        <ColumnGuides model={model} />
        <CurrentPeriodRules model={model} />

        {/* The Platform milestone swimlane strip sits at the top of the lane
            track, above the bars, with a shared hover/focus highlight that wires
            each milestone to the Initiative it belongs to. A milestone is drawn
            ONLY here — the inline per-bar marker was removed so it cannot show up
            twice — so each swimlane marker is also the milestone's item in the
            roving-tabindex widget (task 22.4). */}
        <MilestoneSwimlane
          milestones={swimlane}
          highlightedBarId={highlightedBarId}
          onHighlight={setHighlightedBarId}
          onOpenInitiative={onOpenInitiative}
          rovingFor={(milestone) =>
            itemProps(laneIndex, milestoneFocusIndex(focusItems, milestone))
          }
        />

        {focusItems.map((focusItem, item) => {
          // Milestones are rendered by the swimlane above; only bars render inline
          // in the lane track now. Their focus-grid item index still comes from
          // the shared `focusItems` order, so the milestone the swimlane renders
          // and the bar the track renders share one `(lane, item)` numbering.
          if (focusItem.kind !== 'bar') return null;
          const roving = itemProps(laneIndex, item);
          return (
            <InitiativeBar
              key={`bar-${focusItem.placed.bar.id}`}
              placed={focusItem.placed}
              platformColour={lane.colour}
              // R10.8: the payload resolves the priority record's
              // Elevated_Priority_Flag server-side, so the marker is a read off
              // the bar rather than a master-data lookup here - the same way
              // `platformColour` comes from the legend-derived lane.
              elevatedPriority={focusItem.placed.bar.priorityElevated}
              onOpen={onOpenInitiative}
              roving={roving}
              verticalOffsetPx={barsOffsetPx}
              highlighted={highlightedBarId === focusItem.placed.bar.id}
            />
          );
        })}

        {layout && layout.overflowCount > 0 ? (
          <LaneOverflowIndicator
            unrenderedCount={layout.overflowCount}
            expanded={expanded}
            onToggle={onToggleExpand}
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * The `(lane, item)` item index of a swimlane milestone within the lane's shared
 * {@link LaneFocusItem} order.
 *
 * The swimlane renders only milestones, but the roving focus grid numbers bars
 * and milestones together, so a swimlane marker must find its position in that
 * combined order. Matched on the parent bar id and the milestone's own index
 * within that bar (both carried on the {@link SwimlaneMilestone}), which is the
 * same pair {@link laneFocusItems} keys a milestone item by.
 */
function milestoneFocusIndex(
  focusItems: readonly LaneFocusItem[],
  milestone: SwimlaneMilestone,
): number {
  return focusItems.findIndex(
    (item) =>
      item.kind === 'milestone' &&
      item.placed.bar.id === milestone.parentBarId &&
      item.milestoneIndex === milestone.milestoneIndex,
  );
}

/** Faint vertical column dividers so an empty lane still reads as a grid. */
function ColumnGuides({ model }: { model: CalendarGridModel }): React.JSX.Element {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 grid"
      style={{
        gridTemplateColumns: `repeat(${String(Math.max(model.columns.length, 1))}, minmax(0, 1fr))`,
      }}
    >
      {model.columns.map((column) => (
        <div key={column.index} className="border-line-soft border-r last:border-r-0" />
      ))}
    </div>
  );
}

/**
 * The current-ISO-week and current-month indicators (R10.12), full lane height.
 */
function CurrentPeriodRules({ model }: { model: CalendarGridModel }): React.JSX.Element | null {
  const current = model.currentPeriod;
  if (!current) return null;
  const columnCount = Math.max(model.columns.length, 1);

  const rules: number[] = [];
  if (current.currentMonthColumnIndex !== null) rules.push(current.currentMonthColumnIndex);
  if (current.currentIsoWeekColumnIndex !== null) rules.push(current.currentIsoWeekColumnIndex);
  if (rules.length === 0) return null;

  return (
    <>
      {rules.map((index) => {
        const leftPercent = ((index + 0.5) / columnCount) * 100;
        return (
          <div
            key={index}
            aria-hidden
            className="pointer-events-none absolute inset-y-0"
            style={{
              left: `${String(leftPercent)}%`,
              width: '2px',
              transform: 'translateX(-1px)',
              background: 'var(--risk)',
              opacity: 0.55,
            }}
          />
        );
      })}
    </>
  );
}

/** The small header dot marking a current period column, redundant with the rule (R10.12, R18.8). */
function CurrentPeriodDot(): React.JSX.Element {
  const { t } = useTranslation();
  const style: CSSProperties = { background: 'var(--risk)' };
  return (
    <span
      aria-label={t('calendar.currentPeriod')}
      className="inline-block h-[6px] w-[6px] rounded-full"
      style={style}
    />
  );
}
